import { createServerFn } from "@tanstack/react-start"
import { getRequestHeader } from "@tanstack/react-start/server"
import { env } from "cloudflare:workers"

import { validateTurnstile } from "@/cloudflare/turnstile"
import { logger } from "@/core/logger.server"
import { sendContactMessageEmail } from "@/email/templates.server"
import { parseFormData } from "@/form"

import { consumeContactRate } from "../rate-limit.server"
import { contactFormSchema, MIN_FILL_MS } from "../schema"

export const DEFAULT_CONTACT_RECIPIENT = "admin@kestly.app"
const SUCCESS = "Thanks, your message is on its way. We will reply by email."

export const contactFormAction = createServerFn({ method: "POST" })
  .validator((data: FormData) => parseFormData(contactFormSchema, data))
  .handler(async ({ data }) => {
    const ip = getRequestHeader("CF-Connecting-IP") ?? "unknown"

    // Honeypot: look successful so a bot learns nothing, send nothing.
    if (data.hp_k7x2) {
      logger.warn("contact.honeypot")
      return { success: SUCCESS }
    }

    const fillMs = Number(data.fillMs)
    if (!Number.isFinite(fillMs) || fillMs < MIN_FILL_MS) {
      logger.warn("contact.too_fast")
      throw new Error("That was quick. Please check your message and send it again.")
    }

    if (!consumeContactRate(ip, { max: 5, windowMs: 60 * 60 * 1000 })) {
      logger.warn("contact.rate_limited")
      throw new Error("Too many messages from your network. Please try again later.")
    }

    const secret = env.CLOUDFLARE_TURNSTILE_SECRET_KEY
    if (secret) {
      const isHuman = await validateTurnstile({
        secret,
        token: data["cf-turnstile-response"],
        ip,
      })
      if (!isHuman) {
        logger.warn("contact.turnstile_failed")
        throw new Error("Human verification failed. Please try again.")
      }
    }

    // Optional Worker var/secret; not in the generated env types.
    const to =
      (env as unknown as { CONTACT_RECIPIENT_EMAIL?: string }).CONTACT_RECIPIENT_EMAIL?.trim() ||
      DEFAULT_CONTACT_RECIPIENT
    try {
      await sendContactMessageEmail({
        to,
        name: data.name,
        email: data.email,
        message: data.message,
      })
    } catch (error) {
      // never log the message or the address
      logger.error("contact.send_failed", {
        error_name: error instanceof Error ? error.name : typeof error,
      })
      throw new Error("We could not send your message. Please try again in a few minutes.", {
        cause: error,
      })
    }
    return { success: SUCCESS }
  })
